package com.essntl.features.proposal.domain.use_case

import com.essntl.core.utils.viewmodel.Operation
import com.essntl.features.proposal.domain.model.ProposalServiceModel
import com.essntl.features.proposal.domain.repository.ProposalRepository
import com.essntl.toastmanager.ToastManager
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.flow.MutableSharedFlow
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.asSharedFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import org.koin.core.annotation.Single

@Single
class UpdateProposalServiceUseCase(
    private val proposalRepository: ProposalRepository,
    private val toastManager: ToastManager,
) {
    data class UseCaseState(
        val loadingSet: Set<String> = emptySet(),
    )

    private val _state = MutableStateFlow(UseCaseState())
    val state = _state.asStateFlow()

    private val _successEvent = MutableSharedFlow<Operation.Update<ProposalServiceModel>>()
    val successEvent = _successEvent.asSharedFlow()

    private val _failureEvent = MutableSharedFlow<Throwable>()
    val failureEvent = _failureEvent.asSharedFlow()

    suspend fun update(
        proposalService: ProposalServiceModel,
        ignoreLoading: Boolean = false,
        showSuccessToast: Boolean = true,
        onSuccess: (ProposalServiceModel) -> Unit = {},
    ) {
        try {
            if (proposalService.id in _state.value.loadingSet && !ignoreLoading)
                return

            if (proposalService.isEmpty)
                return

            _state.update {
                it.copy(
                    loadingSet = _state.value.loadingSet + proposalService.id
                )
            }

            proposalRepository.updateProposalService(proposalService)
                .onSuccess { newProposalService ->
                    _state.update {
                        it.copy(
                            loadingSet = _state.value.loadingSet - proposalService.id
                        )
                    }

                    _successEvent.emit(Operation.Update(newProposalService))
                    onSuccess(newProposalService)

                    if (showSuccessToast)
                        toastManager.success(description = "Proposal updated successfully")
                }
                .onFailure { e ->
                    e.printStackTrace()
                    _state.update {
                        it.copy(
                            loadingSet = _state.value.loadingSet - proposalService.id
                        )
                    }

                    _failureEvent.emit(e)
                }
        } catch (e: CancellationException) {
            _state.update {
                it.copy(
                    loadingSet = it.loadingSet - proposalService.id
                )
            }

            _failureEvent.emit(e)
        }
    }
}